import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Mail, Film, Clapperboard, Smartphone, Camera, Plane, MessageSquare, Map, Video, Package } from 'lucide-react';
import { AnimatedSection, StaggerChildren, StaggerItem } from '@/components/AnimatedComponents';
import ProjectCarousel from '@/components/ProjectCarousel';

const CAL_LINK = 'https://cal.com/terrywebster/30min';

// ── POPULATE ME: swap vimeoId (or videos array) with real Vimeo IDs ──
const showcases = [
  {
    id: 1,
    title: 'Meet Terry',
    tag: 'The Filmmaker',
    description:
      "17 years behind the lens. Broadcast television, brand campaigns and documentary — for international networks, national brands, and now for resorts and destination properties across Australia, Bali and the Maldives. Owner-direct, one point of contact, one small crew. This is a quick introduction to how I work and what you can expect when you bring me in.",
    image:
      'https://i.vimeocdn.com/video/1380529695-9e8dce7c045dcb5a079b9caca4d29d87839bbf01ad2a2b76d96f375d97b0d579-d?f=jpg&region=us',
    vimeoId: '681086047',
    videos: null,
    services: ['17+ Years Experience', 'Broadcast-Grade Craft', 'Owner-Direct'],
  },
  {
    id: 2,
    title: 'A Day at Wyndham Surfers Paradise',
    tag: 'Wyndham Resorts',
    description:
      "A grommet's day at the resort — surf, eat, pool, nap, repeat. First-person storytelling that puts viewers inside the experience, not outside looking in. Real guest voice, real property, zero corporate polish.",
    image:
      'https://i.vimeocdn.com/video/537765121-defae13f6257ecd0a4b09c37e9015fd5e21a4e5a4208c1ac6857cd7e3267610c-d?f=jpg&region=us',
    vimeoId: '140996888',
    videos: null,
    services: ['Surf Cinematography', 'Drone', 'Character-Led Story'],
  },
  {
    id: 3,
    title: 'The Signature Resort Film',
    tag: 'Hero Brand Film',
    description:
      "The film that does the heavy lifting. A 60–90 second cinematic hero piece that captures what it actually feels like to arrive at your resort — the light, the water, the first cocktail, the exhale. This is the asset that sits on your homepage, opens your campaigns, and makes people stop scrolling and start dreaming.",
    image:
      'https://i.vimeocdn.com/video/1686052441-55de9f628aef1a21bf44e3c6a4119c27275811538620f85d4172231716334994-d?f=jpg&region=us',
    vimeoId: '837525047',
    videos: null,
    services: ['Cinematography', 'Drone', 'Colour Grade', 'Sound Design'],
  },
  {
    id: 4,
    title: 'The Ultimate Family Getaway',
    tag: "Moby's Beachside Retreat",
    description:
      "One campaign. Over a million in reach. Ten years on, still a client — and the film is still working for them. Less is more.",
    image:
      'https://i.vimeocdn.com/video/747349713-6709b7114c62322984909eb287cbce45e8842479a158c2398fb62c6404e3fb8a-d?f=jpg&region=us',
    vimeoId: '307400189',
    videos: null,
    services: ['1M+ Reach', '10+ Year Client', 'Resort Campaign'],
  },
  {
    id: 5,
    title: 'Rooms, Villas & Views',
    tag: 'Accommodation Story',
    description:
      "Guests don't book square metres — they book how a space makes them feel. Slow, deliberate camera moves through your best suites and villas, golden-hour aerials of the property, and the details your photos can't show: curtains moving in the breeze, light shifting across the water. Content built to justify your rate card.",
    image:
      'https://i.vimeocdn.com/video/729837032-2643012587cf010c600dff1a373ed03d40ab305b9a1bac3c6a4b5914be630c5f-d?f=jpg&region=us',
    vimeoId: '293252471',
    videos: null,
    services: ['Aerial Cinematography', 'Interiors', 'Gimbal Work'],
  },
  {
    id: 6,
    title: 'Spa, Wellness & Escape',
    tag: 'Experience Story',
    description:
      "The wellness traveller is the highest-spending guest you have. Calm, considered visuals of your spa, wellness and quiet spaces — shot to make a stressed executive in Sydney, Singapore or London book on the spot. Filmed at Shadow Mentawais Eco Surf Camp for the Australasian Educare retreat. This is emotion-first filmmaking: less brochure, more 'I need this now'.",
    image:
      'https://i.vimeocdn.com/video/597383386-49afb306a041217a2e53c426e0dfd41b14c61a1181f3eb9ecd308c8362142c23-d?f=jpg&region=us',
    vimeoId: '187578906',
    videos: null,
    services: ['Cinematography', 'Natural Sound', 'Colour Grade'],
  },
  {
    id: 7,
    title: 'YouTube Videos',
    tag: 'Long-Form & Educational',
    description:
      "Beyond the hero brand film, resorts and destination brands win serious ongoing attention with long-form YouTube content — the videos your guests actually search for. How-to guides, behind-the-scenes stories, destination guides, guest experiences. Built to rank, built to be watched, and built to work for you year after year.",
    image:
      'https://i.vimeocdn.com/video/1767420805-0fc60634243ce0f52452487d935ed4bbfc219c1c8f0444e56b7cb7fc70f20109-d?f=jpg&region=us',
    vimeoId: '893598201',
    videos: null,
    services: ['Long-Form Edit', 'Scripting & Direction', 'YouTube Optimisation'],
  },
];

const deliverables = [
  { icon: Film, title: 'Cinematic Hero Film', text: 'A 60–90 second brand film for your website, campaigns and trade shows.' },
  { icon: Clapperboard, title: 'Paid Ad Cutdowns', text: '15s and 30s versions built for Meta, YouTube and Pmax campaigns.' },
  { icon: Smartphone, title: 'Vertical Social Edits', text: 'Reels and TikTok-ready edits your team can post for months.' },
  { icon: Plane, title: 'Drone & Aerials', text: 'Licensed aerial cinematography that shows off your location like nothing else.' },
  { icon: Camera, title: 'Stills Photography', text: 'Campaign-grade stills captured on the same shoot — no second crew needed.' },
  { icon: Package, title: 'Full Usage Rights', text: 'Your content, your channels, no confusing licensing traps.' },
];

const resortClients = [
  'Wyndham',
  'Accor',
  'Hotel Komune',
  'Mobys Beachside Retreat',
  'Blue Lagoon Beach Resort',
  'Shadow Mentawais Eco Surf Camp',
  'Australasian Educare',
];

const processSteps = [
  {
    icon: MessageSquare,
    step: '01',
    title: 'Strategy Call',
    text: "A free 30-minute call. We talk occupancy goals, target guests, shoulder seasons you want to fill, and what's not working with your current content.",
  },
  {
    icon: Map,
    step: '02',
    title: 'Story & Shot Plan',
    text: "I build a full creative treatment and shot plan around your property's strongest assets — sunrise to last cocktail — so every hour on-site counts.",
  },
  {
    icon: Video,
    step: '03',
    title: 'The Shoot',
    text: 'A lean, experienced crew that moves quietly around your guests. Typically 2–4 days on property. No circus, no disruption, no stressed staff.',
  },
  {
    icon: Package,
    step: '04',
    title: 'Delivery & Launch',
    text: 'Graded, mastered and delivered as a complete content suite — hero film, cutdowns, verticals and stills — ready to deploy across every channel.',
  },
];

export default function ResortFilms() {
  useEffect(() => {
    const canonicalUrl = 'https://terrywebster.com.au/films-that-fill-rooms';
    const pageTitle = 'A Story For Your Stay | Resort Film & Photography | Terry Webster';
    const pageDescription = 'Cinematic resort and hotel video production by Terry Webster — 17+ years of broadcast and commercial filmmaking. Hero films, aerials, dining stories and social content that turn lookers into bookers. Sydney-based, filming worldwide.';
    const ogImage = 'https://static.prod-images.emergentagent.com/jobs/32dcb19e-65f6-4db4-9d72-392b7fe4303e/images/2632f88e9ffed9860828929c54aa0d35ad839ddd6f6230d3f6670c9c9bb05ea5.png';

    // Title + description
    const previousTitle = document.title;
    document.title = pageTitle;
    const descMeta = document.querySelector('meta[name="description"]');
    const previousDesc = descMeta?.getAttribute('content');
    descMeta?.setAttribute('content', pageDescription);

    // Helper to upsert a meta tag by attribute (property or name)
    const upsertMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      return { el, prev, created };
    };

    const restore = [];
    // Open Graph
    restore.push({ ...upsertMeta('property', 'og:title', pageTitle), attr: 'property', key: 'og:title' });
    restore.push({ ...upsertMeta('property', 'og:description', pageDescription), attr: 'property', key: 'og:description' });
    restore.push({ ...upsertMeta('property', 'og:url', canonicalUrl), attr: 'property', key: 'og:url' });
    restore.push({ ...upsertMeta('property', 'og:type', 'website'), attr: 'property', key: 'og:type' });
    restore.push({ ...upsertMeta('property', 'og:image', ogImage), attr: 'property', key: 'og:image' });
    // Twitter
    restore.push({ ...upsertMeta('name', 'twitter:card', 'summary_large_image'), attr: 'name', key: 'twitter:card' });
    restore.push({ ...upsertMeta('name', 'twitter:title', pageTitle), attr: 'name', key: 'twitter:title' });
    restore.push({ ...upsertMeta('name', 'twitter:description', pageDescription), attr: 'name', key: 'twitter:description' });
    restore.push({ ...upsertMeta('name', 'twitter:image', ogImage), attr: 'name', key: 'twitter:image' });

    // Canonical
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    const canonicalCreated = !linkCanonical;
    let previousCanonical;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    } else {
      previousCanonical = linkCanonical.getAttribute('href');
    }
    linkCanonical.setAttribute('href', canonicalUrl);

    // JSON-LD — Service schema
    const jsonLd = document.createElement('script');
    jsonLd.type = 'application/ld+json';
    jsonLd.setAttribute('data-testid', 'resort-jsonld');
    jsonLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Resort & Hotel Video Production',
      serviceType: 'Cinematic Video Production for Resorts, Hotels & Destination Brands',
      provider: {
        '@type': 'Person',
        name: 'Terry Webster',
        jobTitle: 'Filmmaker, Cinematographer & Producer',
        url: 'https://terrywebster.com.au',
        image: 'https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/eyoeieqm_Terry_aboutme_photo_02.jpg',
        sameAs: [
          'https://www.instagram.com/tezzawebster',
          'https://www.linkedin.com/in/terry-webster',
          'https://www.youtube.com/@terrywebster9',
        ],
      },
      areaServed: [
        { '@type': 'Country', name: 'Australia' },
        { '@type': 'Place', name: 'Bali, Indonesia' },
        { '@type': 'Country', name: 'Maldives' },
        { '@type': 'Place', name: 'Worldwide' },
      ],
      offers: {
        '@type': 'Offer',
        description: 'Hero brand films, accommodation storytelling, food & beverage, wellness, social content and long-form YouTube — all delivered from a single shoot with full usage rights.',
      },
      url: canonicalUrl,
      image: ogImage,
      description: pageDescription,
    });
    document.head.appendChild(jsonLd);

    return () => {
      document.title = previousTitle;
      if (previousDesc) descMeta?.setAttribute('content', previousDesc);
      restore.forEach(({ el, prev, created, attr, key }) => {
        if (created) {
          document.querySelector(`meta[${attr}="${key}"]`)?.remove();
        } else if (prev) {
          el.setAttribute('content', prev);
        }
      });
      if (canonicalCreated) {
        linkCanonical.remove();
      } else if (previousCanonical) {
        linkCanonical.setAttribute('href', previousCanonical);
      }
      jsonLd.remove();
    };
  }, []);

  return (
    <main data-testid="resort-films-page">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          {/* Poster fallback (shown behind video / while it loads / if Vimeo privacy blocks it) */}
          <img
            src="https://static.prod-images.emergentagent.com/jobs/32dcb19e-65f6-4db4-9d72-392b7fe4303e/images/2632f88e9ffed9860828929c54aa0d35ad839ddd6f6230d3f6670c9c9bb05ea5.png"
            alt="Cinematic luxury resort infinity pool at golden hour"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 w-full h-full">
            <iframe
              src="https://player.vimeo.com/video/1212292391?background=1&autoplay=1&loop=1&muted=1&autopause=0&controls=0&title=0&byline=0&portrait=0"
              title="Resort showreel"
              allow="autoplay; fullscreen; picture-in-picture"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full pointer-events-none"
              frameBorder="0"
              data-testid="resort-hero-video"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
        </div>
        <div className="container-wide relative z-10 pb-20 md:pb-28 pt-40">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="caption mb-4"
          >
            Resort + Destination Films
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl lg:text-8xl font-bold tracking-tighter mb-6"
          >
            A story<br />
            <span className="text-[#007AFF]">for your stay.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[#E4E4E7] text-lg md:text-xl max-w-2xl mb-10"
          >
            Cinematic films, photography and social content that inspire bookings —
            where hospitality meets cinematic excellence.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href={CAL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="resort-hero-book-call-btn"
            >
              <Calendar size={18} />
              Schedule a Chat
            </a>
            <Link
              to="/contact"
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="resort-hero-contact-btn"
            >
              <Mail size={18} />
              Flick Us an Email
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="container-wide">
          <div className="max-w-4xl">
            <AnimatedSection>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter mb-8">
                Resort + Lifestyle
              </h2>
              <p className="text-[#A1A1AA] text-lg md:text-xl leading-relaxed mb-6">
                I&apos;m Terry Webster, an Australian filmmaker and cinematographer. For{' '}
                <span className="text-white font-semibold">17 years</span> I&apos;ve made work for{' '}
                <span className="text-white font-semibold">broadcast television, brand campaigns and documentary</span>
                {' '}— and these days I bring that same approach to resorts, without the agency mark-up or the
                twenty-person crew.
              </p>
              <p className="text-[#A1A1AA] text-lg md:text-xl leading-relaxed mb-10">
                <span className="text-white font-semibold">I work directly with owners, GMs and marketing managers</span> of
                resorts, lodges and destination properties across{' '}
                <span className="text-white font-semibold">Australia, Bali and the Maldives</span>. One point of contact,
                one small crew that moves respectfully around your guests, and one thing to get right:{' '}
                <span className="text-white font-semibold">content that turns lookers into bookers</span>.
              </p>
              <button
                type="button"
                onClick={() => {
                  document.querySelector('[data-testid="resort-showcases"]')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  });
                }}
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="resort-want-more-btn"
              >
                Want More
                <ArrowRight size={16} />
              </button>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Client Logos Marquee */}
      <section
        className="py-16 bg-[#0C0C0C] border-y border-[#27272A] overflow-hidden"
        data-testid="resort-clients-marquee"
      >
        <div className="container-wide mb-8">
          <p className="text-center text-[#71717A] uppercase tracking-[0.3em] text-xs">
            Trusted by resorts &amp; destination brands
          </p>
        </div>
        <div className="flex animate-marquee items-center">
          {[...resortClients, ...resortClients].map((client, index) => (
            <span
              key={`resort-client-${client.replace(/\s+/g, '-').toLowerCase()}-${index}`}
              className="text-[#A1A1AA] text-xl md:text-2xl font-semibold whitespace-nowrap mx-8 md:mx-14 opacity-70 hover:opacity-100 hover:text-white transition-all"
            >
              {client}
            </span>
          ))}
        </div>
      </section>

      {/* Showcases */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="resort-showcases">
        <div className="container-wide">
          <AnimatedSection className="mb-16">
            <p className="caption mb-4">Examples of Work</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter max-w-3xl">
              Every project below started with a brief. Let&apos;s build yours.
            </h2>
          </AnimatedSection>

          <AnimatedSection>
            <ProjectCarousel
              projects={showcases}
              ctaLabel="Discuss This For Your Resort"
              ctaHref={CAL_LINK}
            />
          </AnimatedSection>
        </div>
      </section>

      {/* Why video */}
      <section className="py-24 md:py-32 bg-[#0C0C0C] border-y border-[#27272A]">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-12 lg:gap-16 items-start">
            {/* Left — Vertical Video */}
            <AnimatedSection>
              <div className="flex flex-col items-center lg:items-start w-full lg:sticky lg:top-24">
                <div
                  className="relative w-full max-w-[420px] overflow-hidden rounded-sm bg-black shadow-2xl"
                  style={{ aspectRatio: '9 / 16' }}
                >
                  <iframe
                    src="https://player.vimeo.com/video/1212980702?h=ce16806f7f&autoplay=1&loop=1&muted=1&background=1&controls=0&title=0&byline=0&portrait=0&autopause=0&dnt=1&playsinline=1"
                    title="Social content example — vertical reel"
                    allow="autoplay; fullscreen; picture-in-picture"
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    frameBorder="0"
                    data-testid="why-film-vertical-video"
                  />
                </div>
                <p className="text-[#71717A] text-sm mt-4 leading-relaxed max-w-[420px] text-center lg:text-left">
                  Example: a short-form vertical edit built to hook attention on Instagram
                  and TikTok — then drive traffic back to the full-length hero film on your
                  website.
                </p>
              </div>
            </AnimatedSection>

            {/* Right — Copy + Icons */}
            <AnimatedSection>
              <p className="caption mb-4">Why Film, Why Now</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter mb-8">
                Travellers don&apos;t read anymore. They watch — then they book.
              </h2>
              <p className="text-[#A1A1AA] text-lg leading-relaxed mb-12">
                Every guest screens you on social before booking. A shaky phone clip as your
                best video? That&apos;s your brand. Competitors with cinematic content
                aren&apos;t better resorts. They look like it online. One professional shoot
                works across ads, website, OTAs and sales collateral for years.
              </p>

              {/* Deliverables — one shoot, a full library */}
              <div className="pt-8 border-t border-[#27272A]">
                <p className="caption mb-6">One shoot. A full library of content.</p>
                <StaggerChildren className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="resort-deliverables">
                  {deliverables.map((item) => (
                    <StaggerItem key={item.title}>
                      <div className="border border-[#27272A] p-5 h-full hover:border-[#007AFF] transition-colors">
                        <item.icon size={24} className="text-[#007AFF] mb-3" />
                        <h3 className="text-base font-semibold mb-2">{item.title}</h3>
                        <p className="text-[#A1A1AA] text-sm leading-relaxed">{item.text}</p>
                      </div>
                    </StaggerItem>
                  ))}
                </StaggerChildren>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="container-wide">
          <AnimatedSection className="mb-16">
            <p className="caption mb-4">How It Works</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter max-w-3xl">
              A quiet, respectful production, from start to finish.
            </h2>
          </AnimatedSection>
          <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step) => (
              <StaggerItem key={step.step}>
                <div className="border-t-2 border-[#007AFF] pt-6 h-full">
                  <p className="text-[#52525B] text-sm font-mono mb-4">{step.step}</p>
                  <step.icon size={28} className="text-[#007AFF] mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed">{step.text}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 md:py-44">
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/32dcb19e-65f6-4db4-9d72-392b7fe4303e/images/fa8057a3772120c738a00a9e053d32a3e8f98763990efe7d2df9e963d0555ae3.png"
            alt="Aerial view of overwater resort villas"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#050505]/80" />
        </div>
        <div className="container-wide relative z-10 text-center">
          <AnimatedSection>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-6">
              Your resort has a story<br />worth booking.
            </h2>
            <p className="text-[#E4E4E7] text-lg max-w-xl mx-auto mb-10">
              Book a free 30-minute strategy call. No pitch decks, no pressure —
              just a straight conversation about your property, your guests, and
              what film can do for your occupancy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={CAL_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="resort-final-book-call-btn"
              >
                <Calendar size={18} />
                Book a Free Strategy Call
              </a>
              <Link
                to="/contact"
                className="btn-secondary inline-flex items-center justify-center gap-2"
                data-testid="resort-final-contact-btn"
              >
                <Mail size={18} />
                Send an Enquiry
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
}
